'use client'

import * as React from 'react'
import {
  Command,
  CommandGroup,
  CommandItem,
  CommandList,
} from '@/components/ui/command'
import { Command as CommandPrimitive } from 'cmdk'
import { useQuery } from '@tanstack/react-query'
import { apnAutocompleteApnAutocompletePostOptions } from '@/generated/@tanstack/react-query.gen'
import { z } from 'zod'
import { cn } from '@/lib/utils'
import { useDebounce } from '@/hooks/use-debounce'
import { ChevronDown } from 'lucide-react'
import { isEmpty } from 'lodash'

const apnSample = [
  { Alameda: '74126545' },
  { Alpine: '006121019' },
  { Amador: '004154001000' },
  { Butte: '025090009' },
  { Calaveras: '066015014000' },
  { Colusa: '016270008000' },
  { 'Contra Costa': '098560028' },
  { 'Del Norte': '105300035000' },
  { 'El Dorado': '088400037' },
  { Fresno: '33816123S' },
  { Humboldt: '020027004' },
  { Imperial: '046212003' },
  { Inyo: '01309012' },
  { Kern: '18808208' },
  { Kings: '007410035000' },
  { Lake: '01206918' },
  { Lassen: '001150007000' },
  { 'Los Angeles': '2061002052' },
  { Madera: '049262002000' },
  { Marin: '19909326' },
  { Mariposa: '021110018000' },
  { Mendocino: '03241033' },
  { Merced: '005080004000' },
  { Modoc: '011340017000' },
  { Mono: '033421035000' },
  { Monterey: '243221018000' },
  { Napa: '011540005000' },
  { Nevada: '016520003000' },
  { Placer: '052220030000' },
  { Plumas: '001450013000' },
  { Riverside: '845163021' },
  { Sacramento: '12105600660000' },
  { 'San Benito': '0210900080' },
  { 'San Bernardino': '046239109' },
  { 'San Diego': '3481121411' },
  { 'San Francisco': '1939045' },
  { 'San Joaquin': '24512033' },
  { 'San Luis Obispo': '091327040' },
  { 'San Mateo': '048014080' },
  { 'Santa Barbara': '004008037' },
  { 'Santa Clara': '36927009' },
  { 'Santa Cruz': '07803106' },
  { Shasta: '094380006000' },
  { Sierra: '015052001' },
  { Siskiyou: '003430220' },
  { Solano: '0115082040' },
  { Sonoma: '001173010' },
  { Stanislaus: '018052037000' },
  { Sutter: '13240033' },
  { Tehama: '075250031000' },
  { Trinity: '0171300600' },
  { Tulare: '028110009' },
  { Tuolumne: '043680006000' },
  { Ventura: '1580132105' },
  { Yolo: '033190015000' },
  { Yuba: '044400005000' },
]
const resonseSchema = z.object({
  status: z.string(),
  result: z.array(z.string()).default([]),
  msg: z.string().nullish(),
})

interface Props {
  county: string
  selected: string[]
  setSelected: (value: string[]) => void
  disabled: boolean
  hasAbility?: boolean
  onSearch: ()=>void
  onTab: ()=>void
}

export function ApnSearchApn({
  county,
  selected,
  setSelected,
  disabled,
  hasAbility,
  onSearch,
  onTab,
}: Props) {
  const inputRef = React.useRef<HTMLInputElement>(null)
  const selectedRef = React.useRef<string[]>(selected)
  React.useEffect(() => {
    selectedRef.current = selected
  }, [selected])
  const [open, setOpen] = React.useState(false)
  const [inputValue, setInputValue] = React.useState('')
  const inputDebounce = useDebounce(inputValue, 500)
  const [lstData, setLstData] = React.useState<string[]>([])

  const { data: response, isLoading } = useQuery({
    ...apnAutocompleteApnAutocompletePostOptions({
      body: {
        query_input: inputDebounce,
        county: county,
        session_key: JSON.parse(localStorage.auth).sessionKey,
      },
    }),
    staleTime: 1000 * 60 * 5,
    select: (data) => {
      const result = resonseSchema.parse(data)
      return result
    },
    enabled: inputDebounce.length > 2 && !disabled,
  })

  React.useEffect(() => {
    if (!response) return
    if (inputDebounce !== selected.join(',')) {
      setLstData(response?.result || [])
    }
  }, [response, inputDebounce, selected])

  React.useEffect(() => {
    setInputValue('')
  }, [county])

  const handleKeyDown = React.useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      console.log(e.key)
      const input = inputRef.current
      if (input) {
        if (e.key === 'Escape') {
          input.blur()
          setOpen(false)
        }
        else if (e.key === 'Enter') {
          if (selectedRef.current.length > 0) {
            onSearch()
          }
        }
        else if (e.key === 'Tab') {
          e.preventDefault()
          e.stopPropagation()
          if (selectedRef.current.length > 0) {
            onTab()
          }
        }
      }
    },
    []
  )

  // Function to get APN by county name
  function getApnBySamplyCounty(countyName: string) {
    const result = apnSample.find((item) => Object.keys(item)[0] === countyName)
    return result ? `Sample: ${result[countyName as keyof typeof result]}` : ''
  }

  const handleSelect = (value: string) => {
    if (!selected.includes(value)) {
      setSelected([...selected, value])
    }
    setInputValue('')
    inputRef?.current?.focus()
    // inputRef?.current?.blur()
    // setOpen(false)
  }

  const handleRemove = (value: string) => {
    setSelected(selected.filter((item) => item !== value))
  }

  const isInputValueInFilteredData = lstData.some((item) =>
    item.toLowerCase().includes(inputValue.toLowerCase())
  )
  const hasFilteredData = !isEmpty(lstData) && isInputValueInFilteredData

  React.useEffect(() => {
    inputRef.current?.focus()
  }, [])
  
  return (
    <Command
      onKeyDown={handleKeyDown}
      className={cn(
        'overflow-visible bg-transparent transition-[min-height] duration-200 min-h-[0px]'
      )}
    >
      <div className="text-sm">
        <div className="flex flex-col gap-1">
          <div className="relative">
            <CommandPrimitive.Input
              ref={inputRef}
              disabled={disabled || !hasAbility}
              value={inputValue}
              onValueChange={(v) => setInputValue(v)}
              onBlur={() => setOpen(false)}
              onFocus={() => setOpen(true)}
              placeholder={getApnBySamplyCounty(county)}
              className="flex-1 bg-transparent outline-none placeholder:text-muted-foreground rounded-lg w-full
              focus:outline-none focus:ring-1 focus:ring-primary focus:border-primary
              disabled:cursor-not-allowed disabled:opacity-50"
            />
            <div className="absolute right-2 top-1/2 -translate-y-1/2">
              {isLoading && (
                <div className="h-4 w-4 animate-spin rounded-full border-2 border-primary border-t-transparent" />
              )}
              {!isLoading && hasFilteredData && (
                <ChevronDown
                  className="h-4 w-4 opacity-50 cursor-pointer"
                  onClick={() => inputRef.current?.focus()}
                />
              )}
            </div>
          </div>
        </div>
      </div>

      {open && hasFilteredData ? (
        <div className="relative max-h-[200px] mt-2">
          <CommandList>
            <div className="absolute top-0 w-full rounded-md border bg-popover text-popover-foreground shadow-md outline-none animate-in">
              <CommandGroup className="h-full overflow-auto max-h-[200px]">
                {lstData?.map((value, index) => (
                  <CommandItem
                    value={value}
                    key={`${value}-${index}`}
                    onMouseDown={(e) => {
                      e.preventDefault()
                      e.stopPropagation()
                    }}
                    onSelect={() => handleSelect(value)}
                    className="cursor-pointer py-2"
                  >
                    <p className={selected.includes(value) ? 'font-bold text-primary' : ''}>{value}</p>
                  </CommandItem>
                ))}
              </CommandGroup>
            </div>
          </CommandList>
        </div>
      ) : null}

      <div className="mt-3 flex flex-wrap gap-2">
        {selected.map((apn) => (
          <div key={apn} className="bg-gray-200 px-2 py-1 rounded-md flex items-center">
            <span className="mr-2">{apn}</span>
            <button onClick={() => handleRemove(apn)} className="text-black-500 font-bold">x</button>
          </div>
        ))}
      </div>

    </Command>
  )
}
